import { useSyncExternalStore } from 'react';

/**
 * Whether the help panel is open, and which guide it is showing.
 *
 * A module store rather than state in the shell, for the reason `features/search/open.ts` gives:
 * the rail, the phone's More sheet and any `PanelHelp` on any page can all open it, and none of
 * them are near each other in the tree.
 *
 * ── THE GUIDE SURVIVES CLOSING AND NAVIGATING ────────────────────────────────────────────────
 *
 * A guide is read WHILE doing the thing it describes, and the panel sits over the right of the
 * page. Somebody who closes it to reach the button underneath, presses the button and reopens it
 * should find step four still where they left it, not the index. So closing keeps the slug, and so
 * does navigating — following a link in step two must not lose step three. Only "All guides"
 * goes back to the index.
 *
 * NOT PERSISTED. A reload starts from the index, closed: a help panel that reopened itself on
 * every page load would be a console that greets you with a sidebar.
 */

export interface HelpState {
  open: boolean;
  /** The guide on screen, or `null` for the index. */
  slug: string | null;
}

let state: HelpState = { open: false, slug: null };
const listeners = new Set<() => void>();

function set(next: HelpState): void {
  if (next.open === state.open && next.slug === state.slug) return;
  state = next;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function openHelp(): void {
  set({ ...state, open: true });
}

export function closeHelp(): void {
  set({ ...state, open: false });
}

export function toggleHelp(): void {
  set({ ...state, open: !state.open });
}

/** Open the panel at one guide. What `PanelHelp`'s link and a `guide:` link inside a guide call. */
export function openGuide(slug: string): void {
  set({ open: true, slug });
}

export function showGuideIndex(): void {
  set({ ...state, slug: null });
}

export function useHelp(): HelpState {
  return useSyncExternalStore(
    subscribe,
    () => state,
    () => state,
  );
}

/**
 * Start fetching the panel when somebody looks like they are about to open it — a pointer over
 * the trigger, or focus on it.
 *
 * ON INTENT, NOT ON IDLE. Search warms itself on every page load because Ctrl+K has to be instant
 * and is pressed many times a day. Help is opened rarely, and every guide is in its chunk, so
 * fetching it for every session would spend bandwidth on a panel most sessions never open. Hover
 * gives a few hundred milliseconds' head start, which is enough.
 */
export function warmHelp(): void {
  void import('./HelpPanel');
}
